import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { SEO } from "@/components/SEO";
import { openWhatsAppInquiry } from "@/lib/whatsapp";
import { logError } from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_INQUIRY = { name: "", company_name: "", email: "", phone: "", country: "", inquiry_details: "" };
const EMPTY_CATALOGUE = { name: "", company_name: "", email: "", phone: "", country: "" };
const INPUT_CLS = "rounded-none border-border bg-white py-5 font-sans text-sm";

/* ===== FORM SUBMIT HELPER ===== */
async function submitForm({ endpoint, payload, whatsappLabel, successMsg, setLoading, reset }) {
  if (!payload.name || !payload.email) {
    toast.error("Please fill required fields");
    return;
  }
  setLoading(true);
  openWhatsAppInquiry(payload, whatsappLabel);
  try {
    await axios.post(`${API}${endpoint}`, payload);
    toast.success(successMsg);
    reset();
  } catch (err) {
    logError(`Failed to submit ${whatsappLabel}`, err);
    toast.error("Failed to submit. Please try again.");
  } finally {
    setLoading(false);
  }
}

/* ===== CONTACT INFO ===== */
function ContactInfo() {
  const items = [
    { icon: MapPin, label: "Location", value: "Surat, Gujarat, India" },
    { icon: Phone, label: "Phone", value: "+91 6355402541" },
    { icon: Mail, label: "Email", value: "info@kfprints.com" },
  ];
  return (
    <div className="lg:col-span-2">
      <h2 className="text-2xl font-serif text-foreground mb-8">Get in Touch</h2>
      <div className="space-y-6 mb-12">
        {items.map(({ icon: Icon, label, value }) => (
          <div key={label} className="flex items-start gap-4">
            <Icon size={18} className="text-[#C5A059] mt-1 flex-shrink-0" />
            <div>
              <p className="text-sm font-sans font-medium text-foreground">{label}</p>
              <p className="text-sm font-sans text-muted-foreground mt-1">{value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ===== INQUIRY FORM ===== */
function InquiryForm({ loading, setLoading }) {
  const [form, setForm] = useState(EMPTY_INQUIRY);

  const handleSubmit = (e) => {
    e.preventDefault();
    submitForm({
      endpoint: "/inquiries",
      payload: { ...form, inquiry_type: "general" },
      whatsappLabel: "Inquiry",
      successMsg: "Inquiry submitted! It has also been forwarded to our WhatsApp.",
      setLoading,
      reset: () => setForm(EMPTY_INQUIRY),
    });
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <motion.form key="inquiry" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSubmit} className="space-y-5" data-testid="contact-inquiry-form">
      <div className="grid grid-cols-2 gap-4">
        <Input data-testid="contact-name" placeholder="Name *" value={form.name} onChange={update("name")} className={INPUT_CLS} required />
        <Input data-testid="contact-company" placeholder="Company Name" value={form.company_name} onChange={update("company_name")} className={INPUT_CLS} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Input data-testid="contact-email" placeholder="Email Address *" type="email" value={form.email} onChange={update("email")} className={INPUT_CLS} required />
        <Input data-testid="contact-phone" placeholder="Phone Number" value={form.phone} onChange={update("phone")} className={INPUT_CLS} />
      </div>
      <Input data-testid="contact-country" placeholder="Country" value={form.country} onChange={update("country")} className={INPUT_CLS} />
      <Textarea data-testid="contact-details" placeholder="Inquiry Details" value={form.inquiry_details} onChange={update("inquiry_details")} className="rounded-none border-border bg-white font-sans text-sm min-h-[120px]" />
      <Button type="submit" disabled={loading} data-testid="contact-submit-btn" className="bg-[#0F172A] text-white rounded-none px-8 py-6 text-xs uppercase tracking-[0.15em] font-sans font-medium hover:bg-[#0F172A]/90 w-full">
        {loading ? "Submitting..." : "Submit Inquiry"} <ArrowRight size={14} className="ml-2" />
      </Button>
    </motion.form>
  );
}

/* ===== CATALOGUE FORM ===== */
function CatalogueForm({ loading, setLoading }) {
  const [form, setForm] = useState(EMPTY_CATALOGUE);

  const handleSubmit = (e) => {
    e.preventDefault();
    submitForm({
      endpoint: "/catalogue-request",
      payload: form,
      whatsappLabel: "Catalogue Request",
      successMsg: "Catalogue request submitted! We'll send it to your email.",
      setLoading,
      reset: () => setForm(EMPTY_CATALOGUE),
    });
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <motion.form key="catalogue" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} onSubmit={handleSubmit} className="space-y-5" data-testid="contact-catalogue-form">
      <div className="grid grid-cols-2 gap-4">
        <Input data-testid="cat-name" placeholder="Name *" value={form.name} onChange={update("name")} className={INPUT_CLS} required />
        <Input data-testid="cat-company" placeholder="Company Name" value={form.company_name} onChange={update("company_name")} className={INPUT_CLS} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Input data-testid="cat-email" placeholder="Email Address *" type="email" value={form.email} onChange={update("email")} className={INPUT_CLS} required />
        <Input data-testid="cat-phone" placeholder="Phone Number" value={form.phone} onChange={update("phone")} className={INPUT_CLS} />
      </div>
      <Input data-testid="cat-country" placeholder="Country" value={form.country} onChange={update("country")} className={INPUT_CLS} />
      <Button type="submit" disabled={loading} data-testid="cat-submit-btn" className="bg-[#C5A059] text-[#0F172A] rounded-none px-8 py-6 text-xs uppercase tracking-[0.15em] font-sans font-medium hover:bg-[#C5A059]/90 w-full">
        {loading ? "Submitting..." : "Request Catalogue"} <ArrowRight size={14} className="ml-2" />
      </Button>
    </motion.form>
  );
}

/* ===== TAB SWITCHER ===== */
function FormTabs({ activeTab, setActiveTab }) {
  const tabs = [
    { id: "inquiry", label: "Submit Inquiry" },
    { id: "catalogue", label: "Request Catalogue" },
  ];
  return (
    <div className="flex gap-0 mb-8 border-b border-border">
      {tabs.map((t) => (
        <button
          key={t.id}
          data-testid={`tab-${t.id}`}
          onClick={() => setActiveTab(t.id)}
          className={`px-6 py-3 text-xs font-sans uppercase tracking-[0.15em] transition-colors duration-200 border-b-2 ${
            activeTab === t.id ? "border-[#C5A059] text-foreground" : "border-transparent text-muted-foreground"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* ===== MAIN PAGE ===== */
export default function ContactPage() {
  const [activeTab, setActiveTab] = useState("inquiry");
  const [loading, setLoading] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="contact-page" className="pt-20 lg:pt-24">
      <SEO
        title="Contact KF Prints | Textile Sourcing Inquiries & Catalogue Requests — Surat, India"
        description="Get in touch with KF Prints for textile sourcing, bulk orders, export inquiries and catalogue requests. Based in Surat, India — serving buyers across India and the Gulf. WhatsApp & email support."
        keywords="contact textile manufacturer India, fabric sourcing inquiry, textile catalogue request, Surat textile supplier contact"
        path="/contact"
      />
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="section-divider mb-8" />
          <h1 className="text-5xl md:text-7xl font-serif tracking-tight text-foreground mb-6">Contact Us</h1>
          <p className="text-base md:text-lg font-sans text-muted-foreground max-w-xl leading-relaxed">
            We&apos;re here to support your sourcing and manufacturing needs.
          </p>
        </div>
      </section>
      <section className="py-24 lg:py-32 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-16">
            <ContactInfo />
            <div className="lg:col-span-3">
              <FormTabs activeTab={activeTab} setActiveTab={setActiveTab} />
              {activeTab === "inquiry"
                ? <InquiryForm loading={loading} setLoading={setLoading} />
                : <CatalogueForm loading={loading} setLoading={setLoading} />}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
